/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useQuery } from 'react-query';
import { useIntl } from 'react-intl';
import { getVouchers } from '~src/services/api/contracts';
import {
  getOfferVouchers,
  getContractScreeningOffersData,
} from '~src/services/api/offers';
import { OffersScreeningView } from './OffersScreeningView';
import {
  ScreeningContractLike,
  ScreeningOfferLike,
  ScreeningOfferData,
  ScreeningVoucherResponseLike,
} from './screeningDemoData';

type OffersScreeningContractViewProps = {
  projectId: number;
  tenderId: number;
  contract: ScreeningContractLike;
  offers: ScreeningOfferLike[];
  isScreeningLocked?: boolean;
};

export const OffersScreeningContractView: FC<OffersScreeningContractViewProps> = ({
  projectId,
  tenderId,
  contract,
  offers,

  isScreeningLocked,
}) => {
  const intl = useIntl();

  const [voucherResponsesByOfferId, setVoucherResponsesByOfferId] = useState<
    Record<number, ScreeningVoucherResponseLike[]>
  >({});

  const [isResponsesLoading, setIsResponsesLoading] = useState(false);

  const contractOffers = useMemo(
    () => offers.filter((offer) => offer.contract_id === contract.id),
    [offers, contract.id]
  );

  const contractOfferIds = useMemo(
    () => contractOffers.map((offer) => offer.id),
    [contractOffers]
  );

  // Stable signal so the responses effect only re-runs when the SET of offers
  // changes — not when an offer object reference changes (e.g. after a review
  // dashboard cache patch). Prevents a full responses refetch + spinner flash.
  const contractOfferIdsKey = contractOfferIds.join(',');

  const { data: offersData = [] } = useQuery<ScreeningOfferData[]>(
    ['screening-offers-data', projectId, tenderId, contract.id],
    () =>
      getContractScreeningOffersData({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contract.id),
      }),
    { enabled: !!projectId && !!tenderId && !!contract.id }
  );

  const offerDataById = useMemo(
    () =>
      offersData.reduce<Record<number, ScreeningOfferData>>((acc, item) => {
        acc[item.offer_id] = item;
        return acc;
      }, {}),
    [offersData]
  );

  const {
    data: vouchers = [],
    isLoading: isVouchersLoading,
    isError: isVouchersError,
  } = useQuery<any[]>(
    ['screening-contract-vouchers', projectId, tenderId, contract.id],
    () =>
      getVouchers({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contract.id),
      }),
    {
      enabled: !!projectId && !!tenderId && !!contract.id,
    }
  );

  useEffect(() => {
    let isMounted = true;

    const loadResponses = async () => {
      if (!projectId || !tenderId || !contractOfferIds.length) {
        setVoucherResponsesByOfferId({});
        return;
      }

      setIsResponsesLoading(true);

      try {
        const entries = await Promise.all(
          contractOfferIds.map(async (offerId) => {
            try {
              const responses = await getOfferVouchers({
                projectId,
                tenderId,
                offerId,
              });

              return [offerId, responses || []] as [
                number,
                ScreeningVoucherResponseLike[]
              ];
            } catch (error) {
              // Demo-safe fallback.
              // If a single offer response request fails, keep the row visible
              // and mark all vouchers as missing until the real Screening API is ready.
              return [offerId, []] as [number, ScreeningVoucherResponseLike[]];
            }
          })
        );

        if (!isMounted) return;

        setVoucherResponsesByOfferId(
          entries.reduce<Record<number, ScreeningVoucherResponseLike[]>>(
            (acc, [offerId, responses]) => {
              acc[offerId] = responses;
              return acc;
            },
            {}
          )
        );
      } finally {
        if (isMounted) {
          setIsResponsesLoading(false);
        }
      }
    };

    loadResponses();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, tenderId, contractOfferIdsKey]);

  if (isVouchersLoading || isResponsesLoading) {
    return <Spin />;
  }

  if (isVouchersError) {
    return (
      <Alert
        type="warning"
        showIcon
        message={intl.formatMessage({
          id: 'screening-contract-vouchers-load-error-message',
        })}
        description={intl.formatMessage({
          id: 'screening-contract-vouchers-load-error-description',
        })}
      />
    );
  }

  return (
    <OffersScreeningView
      projectId={projectId}
      tenderId={tenderId}
      contract={contract}
      offers={offers}
      vouchers={vouchers}
      voucherResponsesByOfferId={voucherResponsesByOfferId}
      offerDataById={offerDataById}
      isScreeningLocked={isScreeningLocked ?? Number(contract.state) >= 6}
    />
  );
};
